/**
 * useAlarmSubscription Hook
 *
 * Subscribes to real-time alarm transition events via WebSocket for a
 * specific site. On receiving `alarm:transition`, invalidates the relevant
 * React Query caches so the Alarms tab and related views auto-refresh.
 *
 * Contract (Phase 4 — 2026-04-18 alarm overhaul):
 * - Event: `alarm:transition` (replaces legacy `alarm:received`/`alarm:resolved`).
 * - Payload: AlarmTransitionEvent — identity-keyed, 7-transition enum,
 *   3-tier severity. Shape settled in PR 4/7; Phase 5 inherits this schema.
 * - Room: site:<uuid>; the UUID is config.sites.site_id (same `id` used by
 *   the route params and useKPISubscription).
 *
 * Initial-state pattern (future Phase 5 consumers):
 *   Consumers that need current alarm state on mount should fetch
 *   GET /api/v1/sites/:siteId/alarms?status=active directly (or via React
 *   Query) and let this hook's invalidation trigger re-fetches on each
 *   transition. This hook deliberately does NOT maintain an in-memory
 *   alarm list — Phase 5's Alarms page does that via React Query.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../services/socket';

/**
 * Payload shape emitted by the WebSocket server on `alarm:transition`.
 * Mirrors the Phase 2 reconciler's `cloud:alarm` publish envelope minus the
 * top-level siteId/customerId routing fields (stripped by the server handler).
 */
export interface AlarmTransitionEvent {
  identity: {
    site_id: string;
    device_id: string;
    condition_key: string;
  };
  transition:
    | 'raised'
    | 'cleared'
    | 'underlying_clear'
    | 'acknowledged'
    | 'state_change'
    | 'communication_loss'
    | 'communication_restored';
  severity: 'critical' | 'warning' | 'info';
  condition_label: string;
  ts: string;
  event_id: string;
  // Reconciler may include these on acknowledged/cleared transitions; UI
  // should surface both simultaneously when set (dual-state is legitimate).
  cleared_at?: string;
  acked_at?: string;
  [key: string]: unknown;
}

export function useAlarmSubscription(siteId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!siteId) return;

    const socket = getSocket();
    if (!socket) return;

    // Join site room (idempotent — safe even if useKPISubscription already joined)
    socket.emit('subscribe:site', siteId);

    const handleTransition = (_event: AlarmTransitionEvent) => {
      // The server only emits to rooms this socket is subscribed to, so we
      // invalidate unconditionally rather than filtering by siteId again.
      // Invalidate both the dedicated alarm queries (Phase 5 UI) and the
      // legacy project-scoped query that still surfaces alarm summaries.
      queryClient.invalidateQueries({ queryKey: ['alarms', siteId] });
      queryClient.invalidateQueries({ queryKey: ['project', siteId] });
    };

    socket.on('alarm:transition', handleTransition);

    return () => {
      socket.off('alarm:transition', handleTransition);
    };
  }, [siteId, queryClient]);
}
